import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Camera, Star, CheckCircle, XCircle, ChevronRight, Car, MapPin, Calendar } from 'lucide-react';

const RideDriverProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [driverProfile, setDriverProfile] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      setLoading(true);
      const [profileRes, driverRes, appRes, ridesRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', user.id).single(),
        supabase.from('ride_driver_profiles').select('*').eq('user_id', user.id).maybeSingle(),
        supabase
          .from('driver_applications')
          .select('vehicle_make, vehicle_color, vehicle_plate, license_number, vehicle_type')
          .eq('user_id', user.id)
          .eq('driver_type', 'ride')
          .eq('status', 'approved')
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('ride_requests')
          .select('id')
          .eq('driver_id', user.id)
          .eq('status', 'completed'),
      ]);

      if (profileRes.data) setProfile(profileRes.data);
      
      const completedRides = ridesRes.data?.length || 0;
      
      // Get cancellation count
      const { data: cancelledData } = await supabase
        .from('ride_requests')
        .select('id')
        .eq('driver_id', user.id)
        .eq('status', 'cancelled')
        .eq('cancelled_by', user.id);
      
      const cancelledRides = cancelledData?.length || 0;
      const totalAttempted = completedRides + cancelledRides;
      
      // Get rating data
      const { data: ratingsData } = await supabase
        .from('ride_ratings')
        .select('rating')
        .eq('driver_id', user.id);
      
      const avgRating = ratingsData && ratingsData.length > 0
        ? ratingsData.reduce((sum, r) => sum + r.rating, 0) / ratingsData.length
        : driverRes.data?.rating || 0;

      const dp: any = driverRes.data || {};
      const app: any = appRes.data || {};

      setDriverProfile({
        ...dp,
        vehicle_make: dp.vehicle_make || app.vehicle_make || null,
        vehicle_color: dp.vehicle_color || app.vehicle_color || null,
        vehicle_plate: dp.vehicle_plate || app.vehicle_plate || null,
        license_number: dp.license_number || app.license_number || null,
        vehicle_type: dp.ride_types_allowed?.[0] || app.vehicle_type || null,
        computed_rating: avgRating,
        computed_total_rides: completedRides,
        computed_acceptance_rate: totalAttempted > 0 ? Math.round((completedRides / totalAttempted) * 100) : 100,
        computed_cancellation_rate: totalAttempted > 0 ? Math.round((cancelledRides / totalAttempted) * 100) : 0,
      });
      setLoading(false);
    };
    fetchData();
  }, [user]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/avatar.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('avatars').upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(filePath);
      await supabase.from('profiles').update({ avatar_url: publicUrl }).eq('id', user.id);
      setProfile((prev: any) => ({ ...prev, avatar_url: publicUrl }));
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
      </div>
    );
  }

  const rating = driverProfile?.computed_rating || 0;
  const totalRides = driverProfile?.computed_total_rides || 0;
  const acceptanceRate = driverProfile?.computed_acceptance_rate ?? 100;
  const cancellationRate = driverProfile?.computed_cancellation_rate ?? 0;

  const rideTypeLabel = (() => {
    const t = driverProfile?.vehicle_type;
    if (t === 'economy') return 'Economy';
    if (t === 'comfort') return 'Comfort';
    if (t === 'comfort_plus') return 'Comfort+';
    if (t === 'comfort_xl') return 'Comfort XL';
    return t || 'Not set';
  })();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero banner */}
      <div className="relative h-40 bg-gradient-to-br from-[#1a1a2e] to-[#16213e] overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          background: 'radial-gradient(circle at 30% 50%, rgba(59,130,246,0.3), transparent 70%)'
        }} />
      </div>

      {/* Avatar */}
      <div className="flex flex-col items-center -mt-16 relative z-10">
        <div className="relative">
          <div className="w-28 h-28 rounded-full border-4 border-white shadow-lg overflow-hidden bg-muted">
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground text-3xl font-bold">
                {profile?.full_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
          </div>
          <label className="absolute bottom-0 right-0 w-9 h-9 bg-foreground text-background rounded-full flex items-center justify-center cursor-pointer shadow-md hover:opacity-80 transition-opacity">
            <Camera className="w-4 h-4" />
            <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" disabled={uploading} />
          </label>
        </div>
        <h2 className="text-xl font-bold mt-3">{profile?.full_name || 'Driver'}</h2>
        <span className="text-xs text-muted-foreground mt-0.5 px-3 py-1 bg-muted rounded-full">{rideTypeLabel} Driver</span>
      </div>

      {/* Stats row */}
      <div className="flex justify-around py-6 px-4 mt-2">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <span className="text-2xl font-bold">{rating > 0 ? rating.toFixed(2) : 'N/A'}</span>
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
          </div>
          <p className="text-xs text-muted-foreground mt-1">Star rating</p>
        </div>
        <div className="h-10 w-px bg-border" />
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <span className="text-2xl font-bold">{acceptanceRate}%</span>
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-xs text-muted-foreground mt-1">Acceptance</p>
        </div>
        <div className="h-10 w-px bg-border" />
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <span className="text-2xl font-bold">{cancellationRate}%</span>
            <XCircle className="w-5 h-5 text-red-500" />
          </div>
          <p className="text-xs text-muted-foreground mt-1">Cancellation</p>
        </div>
      </div>

      {/* Driver details */}
      <div className="px-4 space-y-0">
        <div className="flex items-center justify-between py-4 border-t border-border">
          <div className="flex items-center gap-3">
            <Car className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Vehicle</p>
              <p className="font-medium text-sm">
                {[driverProfile?.vehicle_make, driverProfile?.vehicle_color].filter(Boolean).join(' — ') || 'Not set'}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between py-4 border-t border-border">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">License Plate</p>
              <p className="font-medium text-sm">{driverProfile?.vehicle_plate || 'Not set'}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between py-4 border-t border-border">
          <div className="flex items-center gap-3">
            <Car className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Total Rides</p>
              <p className="font-medium text-sm">{totalRides}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between py-4 border-t border-b border-border">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Member Since</p>
              <p className="font-medium text-sm">
                {driverProfile?.created_at 
                  ? new Date(driverProfile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) 
                  : profile?.created_at 
                    ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                    : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RideDriverProfile;
